import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Plus, RefreshCw, Trash, X } from 'lucide-react';
import { createContext, useContext, useEffect, useState } from 'react';
import { ADAPTER_LABELS, formatTokens, type Adapter, type ModelCapabilities, type ModelDTO, type ProviderDTO } from '@harness/shared';
import { Modal, PageHeader, Seg, Spinner, Switch } from '../../components/ui';
import { api, ApiError, errorText } from '../../lib/api';
import { relTime } from '../../lib/format';
import { qk } from '../../lib/keys';
import { toast } from '../../lib/store';
import { MfaRequired } from './ServerPage';

const ADAPTERS: Adapter[] = ['openai-compatible', 'anthropic', 'openai', 'google', 'bedrock', 'azure-openai'];
const DEFAULT_URLS: Partial<Record<Adapter, string>> = {
  anthropic: 'https://api.anthropic.com',
  openai: 'https://api.openai.com/v1',
  google: 'https://generativelanguage.googleapis.com/v1beta/openai',
};
const URL_HINTS: Partial<Record<Adapter, string>> = {
  'openai-compatible': 'http://10.0.0.12:8000/v1 · Ollama: http://host:11434/v1',
  'azure-openai': 'https://<resource>.openai.azure.com/openai/v1',
  bedrock: 'Bedrock ships in roadmap phase 3',
};

const price = (m: ModelDTO) =>
  m.priceIn == null && m.priceOut == null
    ? '—'
    : `${(m.priceIn ?? 0).toFixed(2)}${m.priceCachedIn != null ? ` (cached ${m.priceCachedIn.toFixed(2)})` : ''} · ${(m.priceOut ?? 0).toFixed(2)}`;
const toolsLabel = (t: ModelCapabilities['tools']) => (t === 'native' ? 'Native' : t === 'prompt' ? 'Prompt-based' : '—');

/**
 * Whose keys the page manages: your own (/api/providers) or, in the admin section, a person's
 * (/api/admin/users/<id>/providers). Same UI and dialogs either way.
 */
interface Scope {
  base: string;
  keys: { providers: readonly unknown[]; models: readonly unknown[] };
  forName: string | null;
}
const OWN: Scope = { base: '/api', keys: { providers: qk.providers, models: qk.models }, forName: null };
const ScopeContext = createContext<Scope>(OWN);
export const adminKeyScope = (userId: string, name: string): Scope => ({
  base: `/api/admin/users/${userId}`,
  keys: { providers: ['admin', 'providers', userId], models: ['admin', 'models', userId] },
  forName: name,
});

export default function ProvidersPage({ scope = OWN }: { scope?: Scope }) {
  return (
    <ScopeContext.Provider value={scope}>
      <ProvidersInner />
    </ScopeContext.Provider>
  );
}

function ProvidersInner() {
  const qc = useQueryClient();
  const scope = useContext(ScopeContext);
  const { data: providers = [], isLoading } = useQuery({ queryKey: scope.keys.providers, queryFn: () => api.get<ProviderDTO[]>(`${scope.base}/providers`) });
  const [selId, setSelId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [testing, setTesting] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [editModel, setEditModel] = useState<ModelDTO | null>(null);
  const [addModel, setAddModel] = useState('');
  const [confirmRemove, setConfirmRemove] = useState(false);
  const prov = providers.find((p) => p.id === selId) ?? providers[0];

  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: scope.keys.providers });
    await qc.invalidateQueries({ queryKey: scope.keys.models });
  };

  const test = async () => {
    if (!prov) return;
    setTesting(true);
    try {
      await api.post(`${scope.base}/providers/${prov.id}/test`);
      await refresh();
    } catch (e) {
      toast(errorText(e), 'error');
    } finally {
      setTesting(false);
    }
  };

  const remove = async () => {
    if (!prov) return;
    try {
      await api.del(`${scope.base}/providers/${prov.id}`);
      setConfirmRemove(false);
      setSelId(null);
      await refresh();
      toast(`${prov.name} removed`);
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };

  const manualModel = async () => {
    if (!prov || !addModel.trim()) return;
    try {
      await api.post(`${scope.base}/providers/${prov.id}/models`, { modelName: addModel.trim() });
      setAddModel('');
      await refresh();
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };

  return (
    <div className="settings-content">
      <PageHeader
        title={scope.forName ? `${scope.forName}'s providers` : 'Providers & models'}
        sub={
          scope.forName
            ? `Keys and models ${scope.forName} can use in their projects.`
            : 'Your keys: every project you create can use them. An admin may also add or replace keys for you. Hosted APIs and self-hosted endpoints are interchangeable.'
        }
      >
        <button className="btn btn-primary btn-lg" onClick={() => setAdding(true)}>
          <Plus className="icon" />
          Add provider
        </button>
      </PageHeader>

      {isLoading ? (
        <Spinner />
      ) : providers.length === 0 ? (
        <div className="card" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-start' }}>
          <h4>No providers yet</h4>
          <div className="muted">Add Anthropic, OpenAI or any OpenAI-compatible endpoint (Ollama, vLLM, LM Studio, OpenRouter…). Local endpoints need no key.</div>
          <button className="btn btn-primary" onClick={() => setAdding(true)}>
            Add provider
          </button>
        </div>
      ) : (
        prov && (
          <div className="prov-layout">
            <div className="prov-list card">
              {providers.map((p) => (
                <button key={p.id} className="prov-item" aria-pressed={p.id === prov.id} onClick={() => setSelId(p.id)}>
                  <span className="n">{p.name}</span>
                  <span style={{ fontSize: 12, opacity: 0.8 }}>
                    {ADAPTER_LABELS[p.adapter]} · {p.models.length} model{p.models.length === 1 ? '' : 's'}
                  </span>
                </button>
              ))}
            </div>
            <div className="prov-detail card">
              <div className="row" style={{ gap: 10, flexWrap: 'nowrap' }}>
                <div style={{ font: '900 26px var(--font-heading)', flex: 1, minWidth: 0 }} className="ellipsis">
                  {prov.name}
                </div>
                <span className="tag tag-neutral">{prov.selfHosted ? 'Self-hosted' : 'Hosted'}</span>
                <button className="btn btn-ghost" onClick={() => setRenaming(true)}>
                  Rename
                </button>
                <button className="btn btn-icon sm" title="Remove provider" onClick={() => setConfirmRemove(true)}>
                  <Trash className="icon" />
                </button>
              </div>
              <div className="grid-2">
                <div className="field">
                  <label>Adapter</label>
                  <input className="input" readOnly value={ADAPTER_LABELS[prov.adapter]} />
                </div>
                <div className="field">
                  <label>Base URL</label>
                  <input className="input mono" readOnly value={prov.baseUrl} />
                </div>
                <div className="field">
                  <label>API key (encrypted, never shown again)</label>
                  <div className="row" style={{ flexWrap: 'nowrap' }}>
                    <input className="input mono" readOnly value={prov.keyHint ?? 'None (local)'} />
                    <button className="btn btn-secondary" onClick={() => setReplacing(true)}>
                      Replace
                    </button>
                  </div>
                </div>
                <div className="field">
                  <label>Connection</label>
                  <div className="row" style={{ minHeight: 42, flexWrap: 'nowrap' }}>
                    <button className="btn btn-secondary" onClick={test} disabled={testing}>
                      {testing ? <Spinner /> : <RefreshCw className="icon" />}
                      Test connection
                    </button>
                    <span style={{ fontSize: 13, color: testing ? 'var(--color-neutral-700)' : prov.lastTest?.ok ? 'var(--color-text)' : prov.lastTest ? 'var(--color-accent-700)' : 'var(--color-neutral-700)' }} className="ellipsis" title={prov.lastTest?.message}>
                      {testing
                        ? 'Calling /models and a 1-token completion…'
                        : prov.lastTest
                          ? prov.lastTest.ok
                            ? `OK · ${prov.lastTest.latencyMs} ms · ${prov.lastTest.message} · ${relTime(prov.lastTest.at).toLowerCase()}`
                            : prov.lastTest.message
                          : 'Not tested yet'}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <div className="row" style={{ alignItems: 'baseline' }}>
                  <div style={{ font: '900 18px var(--font-heading)', flex: 1 }}>Models</div>
                  <span className="muted" style={{ fontSize: 12 }}>
                    {prov.discovery === 'auto' ? 'Auto-discovered from /models' : 'Entered manually'}
                  </span>
                </div>
                <div className="table-wrap" style={{ marginTop: 8 }}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Model</th>
                        <th>Context</th>
                        <th>Max out</th>
                        <th>Tools</th>
                        <th>Vision</th>
                        <th>Reasoning</th>
                        <th>$ / M in · out</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {prov.models.map((m) => (
                        <tr key={m.id}>
                          <td className="mono" style={{ fontWeight: 600, fontSize: 12.5 }}>
                            {m.modelName}
                            {m.capabilities.kind === 'embedding' && <span className="tag tag-neutral sm" style={{ marginLeft: 6 }}>embedding</span>}
                          </td>
                          <td>{formatTokens(m.contextWindow)}</td>
                          <td>{m.capabilities.kind === 'embedding' ? '—' : formatTokens(m.maxOutput)}</td>
                          <td>{toolsLabel(m.capabilities.tools)}</td>
                          <td>{m.capabilities.vision ? 'Yes' : '—'}</td>
                          <td>{m.capabilities.reasoning === 'none' ? '—' : m.capabilities.reasoning === 'adaptive' ? 'Adaptive' : 'Budget'}</td>
                          <td>{price(m)}</td>
                          <td>
                            <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => setEditModel(m)}>
                              Edit
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <form
                  className="row"
                  style={{ marginTop: 10, flexWrap: 'nowrap' }}
                  onSubmit={(e) => {
                    e.preventDefault();
                    void manualModel();
                  }}
                >
                  <input className="input mono" style={{ maxWidth: 320 }} placeholder="model id, e.g. qwen2.5-coder:32b" aria-label="Model id" value={addModel} onChange={(e) => setAddModel(e.target.value)} />
                  <button className="btn btn-secondary" type="submit" disabled={!addModel.trim()}>
                    + Add model manually
                  </button>
                </form>
              </div>
            </div>
          </div>
        )
      )}

      {adding && (
        <AddProviderDialog
          onClose={() => setAdding(false)}
          onSaved={async (p) => {
            setAdding(false);
            await refresh();
            setSelId(p.id);
            toast(`${p.name} added · available to every project`);
          }}
        />
      )}
      {replacing && prov && <ReplaceKeyDialog provider={prov} onClose={() => setReplacing(false)} onSaved={refresh} />}
      {renaming && prov && <RenameDialog provider={prov} onClose={() => setRenaming(false)} onSaved={refresh} />}
      {editModel && <ModelDialog model={editModel} onClose={() => setEditModel(null)} onSaved={refresh} />}
      <Modal open={confirmRemove} onClose={() => setConfirmRemove(false)} label="Remove provider">
        <div className="dialog-title">Remove {prov?.name}?</div>
        <div className="dialog-body">Agents using its models will need another model. The stored key is deleted.</div>
        <div className="dialog-actions">
          <button className="btn btn-ghost" onClick={() => setConfirmRemove(false)}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={remove}>
            Remove provider
          </button>
        </div>
      </Modal>
    </div>
  );
}

function AddProviderDialog({ onClose, onSaved }: { onClose: () => void; onSaved: (p: ProviderDTO) => void }) {
  const scope = useContext(ScopeContext);
  const [adapter, setAdapter] = useState<Adapter>('openai-compatible');
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [key, setKey] = useState('');
  const [headers, setHeaders] = useState<{ k: string; v: string }[]>([]);
  const [state, setState] = useState<'idle' | 'busy' | 'ok' | 'saving'>('idle');
  const [msg, setMsg] = useState('');
  const [mfa, setMfa] = useState(false);

  useEffect(() => {
    setUrl(DEFAULT_URLS[adapter] ?? '');
    setState('idle');
    setMsg('');
  }, [adapter]);

  const hdrs = () => Object.fromEntries(headers.filter((h) => h.k.trim()).map((h) => [h.k.trim(), h.v]));
  const primary = async () => {
    if (!url.trim()) {
      setMsg('Enter a base URL first.');
      return;
    }
    if (state === 'ok') {
      setState('saving');
      setMfa(false);
      try {
        const p = await api.post<ProviderDTO>(`${scope.base}/providers`, { adapter, name: name.trim() || undefined, baseUrl: url.trim(), apiKey: key || undefined, headers: hdrs() });
        onSaved(p);
      } catch (e) {
        setState('ok');
        if (e instanceof ApiError && e.code === 'mfa_required') setMfa(true);
        else setMsg(errorText(e));
      }
      return;
    }
    setState('busy');
    setMsg('');
    try {
      const r = await api.post<{ ok: boolean; latencyMs?: number; models?: string[]; message?: string; discovery?: string }>(`${scope.base}/providers/test`, {
        adapter,
        baseUrl: url.trim(),
        apiKey: key || undefined,
        headers: hdrs(),
      });
      if (r.ok) {
        setState('ok');
        setMsg(`Connected · ${r.models?.length ?? 0} models discovered${r.discovery === 'manual' ? ' (enter models manually)' : ' from /models'} · ${r.latencyMs} ms`);
      } else {
        setState('idle');
        setMsg(r.message ?? 'Connection failed');
      }
    } catch (e) {
      setState('idle');
      setMsg(errorText(e));
    }
  };

  return (
    <Modal open onClose={onClose} label="Add provider" width={520}>
      <div className="dialog-title">Add provider</div>
      <div className="field">
        <label htmlFor="np-adapter">Adapter</label>
        <select id="np-adapter" className="input" value={adapter} onChange={(e) => setAdapter(e.target.value as Adapter)}>
          {ADAPTERS.map((a) => (
            <option key={a} value={a}>
              {ADAPTER_LABELS[a]}
            </option>
          ))}
        </select>
      </div>
      <div className="field">
        <label htmlFor="np-name">Display name</label>
        <input id="np-name" className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. vLLM · rack-2" />
      </div>
      <div className="field">
        <label htmlFor="np-url">Base URL</label>
        <input id="np-url" className="input mono" value={url} onChange={(e) => (setUrl(e.target.value), setState('idle'))} placeholder={URL_HINTS[adapter] ?? ''} />
      </div>
      <div className="field">
        <label htmlFor="np-key">API key (leave empty for local)</label>
        <input id="np-key" className="input" type="password" autoComplete="off" value={key} onChange={(e) => (setKey(e.target.value), setState('idle'))} />
      </div>
      <div className="field">
        <span className="label">Custom headers (optional)</span>
        {headers.map((h, i) => (
          <div key={i} className="row" style={{ flexWrap: 'nowrap', marginBottom: 6 }}>
            <input className="input mono" placeholder="Header" aria-label="Header name" value={h.k} onChange={(e) => setHeaders(headers.map((x, j) => (j === i ? { ...x, k: e.target.value } : x)))} />
            <input className="input mono" placeholder="Value" aria-label="Header value" value={h.v} onChange={(e) => setHeaders(headers.map((x, j) => (j === i ? { ...x, v: e.target.value } : x)))} />
            <button className="btn btn-icon sm" title="Remove header" onClick={() => setHeaders(headers.filter((_, j) => j !== i))}>
              <X className="icon" />
            </button>
          </div>
        ))}
        <button className="btn btn-ghost" style={{ alignSelf: 'flex-start', fontSize: 12 }} onClick={() => setHeaders([...headers, { k: '', v: '' }])}>
          + Add header
        </button>
      </div>
      {mfa && <MfaRequired />}
      {msg && (
        <div style={{ fontSize: 13, color: state === 'ok' ? 'var(--color-text)' : 'var(--color-accent-700)' }} role="status">
          {msg}
        </div>
      )}
      <div className="dialog-actions">
        <button className="btn btn-ghost" onClick={onClose}>
          Cancel
        </button>
        <button className="btn btn-primary btn-wide" style={{ minWidth: 170 }} onClick={primary} disabled={state === 'busy' || state === 'saving'}>
          {state === 'ok' ? 'Save provider' : state === 'busy' ? 'Testing…' : state === 'saving' ? 'Saving…' : 'Test connection'}
          {state === 'busy' || state === 'saving' ? <Spinner /> : state === 'ok' ? <Check className="icon" /> : <RefreshCw className="icon" />}
        </button>
      </div>
    </Modal>
  );
}

function ReplaceKeyDialog({ provider, onClose, onSaved }: { provider: ProviderDTO; onClose: () => void; onSaved: () => Promise<void> }) {
  const scope = useContext(ScopeContext);
  const [key, setKey] = useState('');
  const [mfa, setMfa] = useState(false);
  const save = async () => {
    try {
      await api.patch(`${scope.base}/providers/${provider.id}`, { apiKey: key });
      await onSaved();
      toast(key ? 'Key replaced · run Test connection to check it' : 'Key removed');
      onClose();
    } catch (e) {
      if (e instanceof ApiError && e.code === 'mfa_required') setMfa(true);
      else toast(errorText(e), 'error');
    }
  };
  return (
    <Modal open onClose={onClose} label="Replace API key">
      <div className="dialog-title">Replace API key · {provider.name}</div>
      <div className="dialog-body">The new key is encrypted at rest and never sent back to the browser. Leave empty to remove the key.</div>
      <input className="input mono" type="password" autoFocus value={key} onChange={(e) => setKey(e.target.value)} aria-label="New API key" />
      {mfa && <MfaRequired />}
      <div className="dialog-actions">
        <button className="btn btn-ghost" onClick={onClose}>
          Cancel
        </button>
        <button className="btn btn-primary" onClick={save}>
          Save key
        </button>
      </div>
    </Modal>
  );
}

function RenameDialog({ provider, onClose, onSaved }: { provider: ProviderDTO; onClose: () => void; onSaved: () => Promise<void> }) {
  const scope = useContext(ScopeContext);
  const [name, setName] = useState(provider.name);
  const [url, setUrl] = useState(provider.baseUrl);
  const save = async () => {
    try {
      await api.patch(`${scope.base}/providers/${provider.id}`, { name: name.trim(), baseUrl: url.trim() !== provider.baseUrl ? url.trim() : undefined });
      await onSaved();
      onClose();
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };
  return (
    <Modal open onClose={onClose} label="Edit provider">
      <div className="dialog-title">Edit provider</div>
      <div className="field">
        <label htmlFor="rn-name">Display name</label>
        <input id="rn-name" className="input" autoFocus value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="rn-url">Base URL</label>
        <input id="rn-url" className="input mono" value={url} onChange={(e) => setUrl(e.target.value)} />
      </div>
      <div className="dialog-actions">
        <button className="btn btn-ghost" onClick={onClose}>
          Cancel
        </button>
        <button className="btn btn-primary" onClick={save} disabled={!name.trim() || !url.trim()}>
          Save
        </button>
      </div>
    </Modal>
  );
}

function ModelDialog({ model, onClose, onSaved }: { model: ModelDTO; onClose: () => void; onSaved: () => Promise<void> }) {
  const scope = useContext(ScopeContext);
  const [f, setF] = useState({
    contextWindow: String(model.contextWindow),
    maxOutput: String(model.maxOutput),
    priceIn: model.priceIn == null ? '' : String(model.priceIn),
    priceOut: model.priceOut == null ? '' : String(model.priceOut),
    priceCachedIn: model.priceCachedIn == null ? '' : String(model.priceCachedIn),
    caps: model.capabilities,
  });
  const setCap = <K extends keyof ModelCapabilities>(k: K, v: ModelCapabilities[K]) => setF({ ...f, caps: { ...f.caps, [k]: v } });
  const n = (s: string) => (s.trim() === '' ? null : Number(s));
  const save = async () => {
    try {
      await api.patch(`${scope.base}/models/${model.id}`, {
        contextWindow: Number(f.contextWindow) || undefined,
        maxOutput: Number(f.maxOutput) || undefined,
        priceIn: n(f.priceIn),
        priceOut: n(f.priceOut),
        priceCachedIn: n(f.priceCachedIn),
        capabilities: f.caps,
      });
      await onSaved();
      toast(`${model.modelName} profile saved`);
      onClose();
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };
  const remove = async () => {
    try {
      await api.del(`${scope.base}/models/${model.id}`);
      await onSaved();
      onClose();
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };
  return (
    <Modal open onClose={onClose} label="Model capability profile" width={560}>
      <div className="kicker">Capability profile</div>
      <div className="dialog-title mono" style={{ fontSize: 17 }}>
        {model.modelName}
      </div>
      <div className="dialog-body">Auto-filled from discovery. The harness uses it to hide unsupported options and to fall back to prompt-based tool calling.</div>
      <div className="grid-2">
        <div className="field">
          <label htmlFor="md-ctx">Context window (tokens)</label>
          <input id="md-ctx" className="input" inputMode="numeric" value={f.contextWindow} onChange={(e) => setF({ ...f, contextWindow: e.target.value.replace(/\D/g, '') })} />
        </div>
        <div className="field">
          <label htmlFor="md-out">Max output (tokens)</label>
          <input id="md-out" className="input" inputMode="numeric" value={f.maxOutput} onChange={(e) => setF({ ...f, maxOutput: e.target.value.replace(/\D/g, '') })} />
        </div>
        <div className="field">
          <label htmlFor="md-pin">$ per M input tokens</label>
          <input id="md-pin" className="input" inputMode="decimal" placeholder="—" value={f.priceIn} onChange={(e) => setF({ ...f, priceIn: e.target.value.replace(/[^\d.]/g, '') })} />
        </div>
        <div className="field">
          <label htmlFor="md-pout">$ per M output tokens</label>
          <input id="md-pout" className="input" inputMode="decimal" placeholder="—" value={f.priceOut} onChange={(e) => setF({ ...f, priceOut: e.target.value.replace(/[^\d.]/g, '') })} />
        </div>
        <div className="field">
          <label htmlFor="md-pcache">$ per M cached input tokens</label>
          <input id="md-pcache" className="input" inputMode="decimal" placeholder="same as input" value={f.priceCachedIn} onChange={(e) => setF({ ...f, priceCachedIn: e.target.value.replace(/[^\d.]/g, '') })} />
          <div className="hint">Input tokens the provider reports as read from its prompt cache.</div>
        </div>
      </div>
      <div className="stack-sm">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <span>Kind</span>
          <Seg size="sm" value={f.caps.kind} onChange={(v) => setCap('kind', v)} options={[{ value: 'chat', label: 'Chat' }, { value: 'embedding', label: 'Embedding' }]} />
        </div>
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <span>Tool calling</span>
          <Seg size="sm" value={f.caps.tools} onChange={(v) => setCap('tools', v)} options={[{ value: 'native', label: 'Native' }, { value: 'prompt', label: 'Prompt-based' }, { value: 'none', label: 'None' }]} />
        </div>
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <span>Reasoning</span>
          <Seg size="sm" value={f.caps.reasoning} onChange={(v) => setCap('reasoning', v)} options={[{ value: 'adaptive', label: 'Adaptive (effort)' }, { value: 'budget', label: 'Token budget' }, { value: 'none', label: 'None' }]} />
        </div>
        {(['vision', 'streaming', 'temperature'] as const).map((k) => (
          <div key={k} className="row" style={{ justifyContent: 'space-between' }}>
            <span>{k === 'vision' ? 'Vision input' : k === 'streaming' ? 'Streaming' : 'Accepts temperature'}</span>
            <Switch on={f.caps[k]} onChange={(v) => setCap(k, v)} label={k} />
          </div>
        ))}
      </div>
      <div className="dialog-actions" style={{ justifyContent: 'space-between' }}>
        <button className="btn btn-ghost btn-danger" onClick={remove}>
          Remove model
        </button>
        <div className="row">
          <button className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={save}>
            Save profile
          </button>
        </div>
      </div>
    </Modal>
  );
}
